import { notFound } from '@tanstack/react-router';
import { getQueryClient } from '../../providers/query';
import { catalogProductsInfiniteQueryOptions } from '@queries/index';
import { buildInitialCatalogQueryArgs } from './components/CatalogCategory/catalogInitialParams';
import { isContentNotFoundError } from '../../api/contentNotFound';
import { assertValidListUrl } from '../../ssrListParams';

/**
 * ЭТАП 2 — честный 404 несуществующей рубрики/группы каталога: бэк
 * (catalogResolver.getProducts) кидает CONTENT_NOT_FOUND на «Нет рубрики»,
 * «Нет фильтра /group/» и «Нет фильтров ?groups=» -> notFound() -> HTTP 404 +
 * контент /404 без редиректа. Общий loader для /catalog/$id и /catalog/$id/r/$.
 *
 * Как у товара/акции: лоадеры TanStack параллельны root-префетчу, поэтому
 * await ensureInfiniteQueryData — дедуп по pristine-ключу с префетчем
 * (catalogInitialParams), лишнего запроса нет; серверный клиент с retry:false.
 * Прочие ошибки 404 не считаем; клиентскую навигацию не блокируем.
 */
export const catalogNotFoundLoader = async ({
  location
}: {
  location: { pathname: string; searchStr?: string };
}): Promise<void> => {
  if (typeof window !== 'undefined') {
    return;
  }
  // Хвост /r/ обязан быть каноническим: чужие ключи, значения сортировки вне
  // списка и /r/page/1 — честный 404, см. src/ssrListParams.ts
  assertValidListUrl('catalog', location.pathname);
  const catalog = buildInitialCatalogQueryArgs(
    location.pathname,
    location.searchStr ?? ''
  );
  if (!catalog) {
    return;
  }
  // Спец-страницы каталога без «своей» рубрики на бэке — их products-запрос
  // может падать штатно, 404 для них был бы ложным
  if (catalog.params.slug === 'services' || catalog.params.slug === 'bar') {
    return;
  }
  const options = catalogProductsInfiniteQueryOptions(
    catalog.params,
    catalog.initialPage
  );
  const queryClient = getQueryClient();
  // Префетч успел упасть раньше — ошибка уже в кэше, без второго запроса
  const settled = queryClient.getQueryState(options.queryKey);
  if (settled?.error) {
    if (isContentNotFoundError(settled.error)) {
      throw notFound();
    }
    return;
  }
  try {
    await queryClient.ensureInfiniteQueryData(options);
  } catch (error) {
    if (isContentNotFoundError(error)) {
      throw notFound();
    }
  }
};
